import { ElMessage } from 'element-plus';

import { compressImageFile, type ImageCompressOptions } from '@cutepet/shared-utils/image-compress';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';
const TOKEN_STORAGE_KEY = 'cutepet-admin-token';
const REQUEST_TIMEOUT_MESSAGE = '请求超时，请稍后重试';
const REQUEST_TIMEOUT_ERROR_CODE = 50000;
const DEFAULT_REQUEST_TIMEOUT_MS = 10_000;

interface ApiEnvelope<T> {
  code: number;
  message: string;
  requestId: string;
  data: T;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly code: number,
    readonly requestId?: string,
  ) {
    super(message);
  }
}

interface ApiRequestOptions extends RequestInit {
  quiet?: boolean;
  timeoutMs?: number;
}

export function saveToken(token: string) {
  window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
}

export function getToken() {
  return window.localStorage.getItem(TOKEN_STORAGE_KEY) ?? '';
}

export function clearToken() {
  window.localStorage.removeItem(TOKEN_STORAGE_KEY);
}

export async function apiRequest<T>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const headers = new Headers(options.headers ?? {});
  headers.set('Content-Type', 'application/json');
  const token = getToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const controller = new AbortController();
  const timeoutId =
    typeof window === 'undefined'
      ? undefined
      : window.setTimeout(
          () => controller.abort(new DOMException('Timeout', 'AbortError')),
          options.timeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS,
        );

  let externalAbortListener: (() => void) | undefined;
  if (options.signal) {
    if (options.signal.aborted) {
      controller.abort(options.signal.reason);
    } else {
      externalAbortListener = () => controller.abort(options.signal?.reason);
      options.signal.addEventListener('abort', externalAbortListener, { once: true });
    }
  }

  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers,
      signal: controller.signal,
    });
    const payload = (await response.json()) as ApiEnvelope<T>;
    if (!response.ok || payload.code !== 0) {
      if (!options.quiet) {
        ElMessage.error(payload.message || '请求失败');
      }
      throw new ApiError(payload.message || '请求失败', payload.code, payload.requestId);
    }

    return payload.data;
  } catch (error) {
    if (controller.signal.aborted && !options.signal?.aborted) {
      if (!options.quiet) {
        ElMessage.error(REQUEST_TIMEOUT_MESSAGE);
      }
      throw new ApiError(REQUEST_TIMEOUT_MESSAGE, REQUEST_TIMEOUT_ERROR_CODE);
    }
    throw error;
  } finally {
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
    }
    if (options.signal && externalAbortListener) {
      options.signal.removeEventListener('abort', externalAbortListener);
    }
  }
}

export function showSuccess(message: string) {
  ElMessage.success(message);
}

export interface UploadedImageResult {
  url: string;
  objectKey: string;
  mimeType: string;
  size: number;
}

type UploadImagePurpose =
  | 'score-record'
  | 'student-avatar'
  | 'pet-food'
  | 'pet-skin'
  | 'classroom-performance';

/**
 * 所有上传场景统一在浏览器端压缩转 webp（q0.88 对齐 cwebp -q 88 约定）。
 * 最长边按展示需求取：头像/小图标用小尺寸，凭证照片保留可读性，皮肤源图约定 2048。
 */
const UPLOAD_COMPRESS_OPTIONS: Record<UploadImagePurpose, ImageCompressOptions> = {
  'score-record': { maxDimension: 1600, quality: 0.88 },
  'student-avatar': { maxDimension: 512, quality: 0.88 },
  'pet-food': { maxDimension: 1024, quality: 0.88 },
  'pet-skin': { maxDimension: 2048, quality: 0.88 },
  'classroom-performance': { maxDimension: 1600, quality: 0.88 },
};

export async function uploadImageFile(file: File, purpose: UploadImagePurpose) {
  const uploadFile = await compressImageFile(file, UPLOAD_COMPRESS_OPTIONS[purpose]);
  const contentBase64 = await readFileAsDataUrl(uploadFile);
  return apiRequest<UploadedImageResult>('/uploads/images', {
    method: 'POST',
    body: JSON.stringify({
      purpose,
      fileName: uploadFile.name,
      mimeType: uploadFile.type,
      contentBase64,
    }),
    timeoutMs: 30_000,
  });
}

function readFileAsDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(new Error('图片读取失败'));
    reader.readAsDataURL(file);
  });
}
